namespace SaAnalyticsUtils {
    //#region Analytics API

    export enum ProductEntityType {
        Course = "Course",
        OrganizationRole = "OrganizationRole",
    }

    export interface AnalyticsProductResponse {
        productId: number;
        productName: string;
        productEntityType: ProductEntityType;
        productEntityId: number;
        memberRegion1Price: number;
        nonMemberRegion1Price: number;
    }

    /**
     * This can be used in combination with the build*Event functions in this class to populate the dataLayer with ecommerce events that have detailed product information.
     * The courseIds parameter is used to retrieve the necessary product information from the backend to populate the events, and can be used in scenarios such as when a user adds a course to their cart, begins checkout, or views their cart.
     */
    export async function listAnalyticsProductsForCourseIds(
        courseIds: number[],
    ): Promise<AnalyticsProductResponse[]> {
        try {
            const url = new URL(
                "/api/shop/products/include-analytics",
                window.location.origin,
            );

            url.searchParams.append("api-version", "1.0");
            url.searchParams.append(
                "productEntityType",
                ProductEntityType.Course,
            );

            for (const courseId of courseIds) {
                url.searchParams.append(
                    "productEntityIds",
                    courseId.toString(),
                );
            }

            const response: { data: AnalyticsProductResponse[] } = await $.ajax(
                {
                    url: url.toString(),
                    method: "GET",
                    dataType: "json",
                },
            );
            if (!response?.data) {
                console.warn(
                    "Received empty response or missing data field from analytics products API.",
                    response,
                );
                return [];
            }

            return response?.data ?? [];
        } catch (error) {
            console.error("Error fetching analytics products:", error);
            return [];
        }
    }

    //#endregion

    //#region DataLayer Basics

    export type DataLayerCheckoutEventName =
        "add_to_cart" | "begin_checkout" | "remove_from_cart" | "view_cart";

    export type DataLayerEventName =
        DataLayerCheckoutEventName | "view_item_list" | "select_item";

    export interface DataLayerEvent<TEcommerce> {
        event: DataLayerEventName;
        ecommerce: TEcommerce;
    }

    export function addToDataLayer(payload: DataLayerEvent<unknown>): void {
        try {
            window.dataLayer = window.dataLayer || [];
            /*
                Clear the previous ecommerce object.
                GTM’s Data Layer Variable mechanism uses recursive merge. 
                Without ecommerce: null, the items array from a previous push persists in memory and bleeds into the next event. 
                source: https://ceaksan.com/en/gtm-enhanced-ecommerce-events, GA4 Documentation does this on every example call: https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_to_cart-gtm
            */
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push(payload);
        } catch (error) {
            // Swallow error for non-critical process, callback is typically a critical process.
            console.error("Error adding to dataLayer:", error);
        }
    }

    //#endregion

    //#region add_to_cart & begin_checkout & view_cart (these three events share the same ecommerce schema)

    export interface DataLayerCheckoutItemInput {
        analyticsProduct: AnalyticsProductResponse;
        language?: string | undefined;
        price: number;
        quantity: number;
    }
    // for Item schema, see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#remove_from_cart_item
    export interface DataLayerCheckoutItem {
        item_id: string;
        item_language?: string | null | undefined;
        item_name: string;
        price: number;
        quantity: number;
        discount?: number | null | undefined;
    }

    export interface DataLayerCheckoutEcommerce {
        currency: string;
        value: number;
        items: DataLayerCheckoutItem[];
    }

    export function buildAddToCartEvent(
        inputs: DataLayerCheckoutItemInput[],
    ): DataLayerEvent<DataLayerCheckoutEcommerce> {
        const items = inputs.map(convertCheckoutInputToCheckoutItem);
        return buildAddToCartEventFromItems(items);
    }

    export function buildAddToCartEventFromItems(
        items: DataLayerCheckoutItem[],
    ): DataLayerEvent<DataLayerCheckoutEcommerce> {
        const totalValue = calculateTotalValue(items);
        return {
            event: "add_to_cart",
            ecommerce: {
                currency: "USD",
                value: totalValue,
                items: items,
            },
        };
    }

    export function buildRemoveFromCartEventFromItems(
        items: DataLayerCheckoutItem[],
    ): DataLayerEvent<DataLayerCheckoutEcommerce> {
        const totalValue = calculateTotalValue(items);
        return {
            event: "remove_from_cart",
            ecommerce: {
                currency: "USD",
                value: totalValue,
                items: items,
            },
        };
    }

    export function buildBeginCheckoutEvent(
        inputs: DataLayerCheckoutItemInput[],
    ): DataLayerEvent<DataLayerCheckoutEcommerce> {
        const items = inputs.map(convertCheckoutInputToCheckoutItem);
        return buildBeginCheckoutEventFromItems(items);
    }

    export function buildBeginCheckoutEventFromItems(
        items: DataLayerCheckoutItem[],
    ): DataLayerEvent<DataLayerCheckoutEcommerce> {
        const totalValue = calculateTotalValue(items);
        return {
            event: "begin_checkout",
            ecommerce: {
                currency: "USD",
                value: totalValue,
                items: items,
            },
        };
    }

    export function buildViewCartEvent(
        inputs: DataLayerCheckoutItemInput[],
    ): DataLayerEvent<DataLayerCheckoutEcommerce> {
        const items = inputs.map(convertCheckoutInputToCheckoutItem);
        return buildViewCartEventFromItems(items);
    }

    export function buildViewCartEventFromItems(
        items: DataLayerCheckoutItem[],
    ): DataLayerEvent<DataLayerCheckoutEcommerce> {
        const totalValue = calculateTotalValue(items);
        return {
            event: "view_cart",
            ecommerce: {
                currency: "USD",
                value: totalValue,
                items: items,
            },
        };
    }

    function convertCheckoutInputToCheckoutItem(
        input: DataLayerCheckoutItemInput,
    ): DataLayerCheckoutItem {
        const { analyticsProduct, language, price, quantity } = input;
        return {
            item_id: String(analyticsProduct.productId),
            item_language: language,
            item_name: analyticsProduct.productName,
            price: price,
            quantity: quantity,
        };
    }

    function calculateTotalValue(items: DataLayerCheckoutItem[]): number {
        return items.reduce(
            (total, item) => total + item.price * item.quantity,
            0,
        );
    }

    export function buildSelectItemEvent(
        itemListId: string,
        itemListName: string,
        inputs: DataLayerViewItemListItemInput[],
    ): DataLayerEvent<DataLayerViewItemListEcommerce> {
        const coursesDataForGA4 = inputs.map((input, index) =>
            convertViewItemListInputToItem(input, index),
        );
        return {
            event: "select_item",
            ecommerce: {
                currency: "USD",
                item_list_id: itemListId,
                item_list_name: itemListName,
                items: coursesDataForGA4,
            },
        };
    }
    //#endregion

    //#region view_item_list

    export interface DataLayerViewItemListEcommerce {
        currency: string;
        item_list_id: string;
        item_list_name: string;
        items: DataLayerViewItemListItem[];
    }

    export type DataLayerViewItemListItemVariant =
        "On-Demand" | "Live" | "On-Demand and Live";

    export interface DataLayerViewItemListItemInput {
        analyticsProduct: AnalyticsProductResponse;
        item_variant: DataLayerViewItemListItemVariant;
    }
    // For schema, see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_item_list
    export interface DataLayerViewItemListItem {
        item_id: string;
        item_name: string;
        quantity: number;
        index: number;
        item_variant: DataLayerViewItemListItemVariant;
        item_brand: string;
        item_category: string;
        member_price?: number; // omit for items that are live only
        non_member_price?: number; // omit for items that are live only
        price?: number; // omit price for items that are live only
    }

    export function buildViewItemListEvent(
        itemListId: string,
        itemListName: string,
        inputs: DataLayerViewItemListItemInput[],
    ): DataLayerEvent<DataLayerViewItemListEcommerce> {
        const coursesDataForGA4 = inputs.map((input, index) =>
            convertViewItemListInputToItem(input, index),
        );

        return buildViewItemListEventFromItems(
            itemListId,
            itemListName,
            coursesDataForGA4,
        );
    }

    export function buildViewItemListEventFromItems(
        itemListId: string,
        itemListName: string,
        items: DataLayerViewItemListItem[],
    ): DataLayerEvent<DataLayerViewItemListEcommerce> {
        return {
            event: "view_item_list",
            ecommerce: {
                currency: "USD",
                item_list_id: itemListId,
                item_list_name: itemListName,
                items: items,
            },
        };
    }

    function convertViewItemListInputToItem(
        input: DataLayerViewItemListItemInput,
        index: number,
    ): DataLayerViewItemListItem {
        const { analyticsProduct, item_variant } = input;
        return {
            item_id: String(analyticsProduct.productId),
            item_name: analyticsProduct.productName,
            quantity: 1,
            index: index,
            item_variant: item_variant,
            item_brand: "Scrum Alliance",
            item_category: "Credentials",
            member_price: analyticsProduct.memberRegion1Price,
            non_member_price: analyticsProduct.nonMemberRegion1Price,
            price: analyticsProduct.nonMemberRegion1Price,
        };
    }

    //#endregion
}

globalThis.SaAnalyticsUtils = SaAnalyticsUtils;
